import React, { useEffect } from 'react'
import { subscribe } from 'react-contextual'
import { Heading, Modal } from 'components'
import Close from 'views/components/atoms/Svgs/Close'
import ViewStatementProvider from './ViewStatementProvider'
import ViewStatementActions from './ViewStatementActions/ViewStatementActions'
import ViewStatementContent from './ViewStatementContent/ViewStatementContent'
import ViewStatementControls from './ViewStatementControls/ViewStatementControls'
import styles from './ViewStatement.module.scss'

interface ViewStatementProps {
  onClose: () => void
  isOpen: boolean
  title?: string
}

const ViewStatement = ({ onClose, isOpen, title = 'View Statement' }: ViewStatementProps): React.ReactElement => {
  // Modal never passes react-modal an `onRequestClose`, so Escape does nothing on its own. FocusMode
  // used to give us this, so wire it up here rather than lose the behaviour.
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  return (
    // Modal's own close button is absolutely positioned against the modal box, which leaves it
    // floating above the header actions. Render our own inline with them instead.
    <Modal isOpen={isOpen} width="lg" padding="sm" showClose={false} className={styles['statement-modal']}>
      <header className={styles.header}>
        <Heading as="h3" size="a3" weight="medium">
          {title}
        </Heading>

        <div className={styles['header-actions']}>
          <ViewStatementActions />

          <button type="button" className={styles.close} onClick={onClose} aria-label="Close statement">
            <Close width={32} height={32} aria-hidden />
          </button>
        </div>
      </header>

      <ViewStatementControls />
      <ViewStatementContent />
    </Modal>
  )
}

export default subscribe([ViewStatementProvider], ({ onClose, isOpen }: any) => ({
  onClose,
  isOpen,
}))(ViewStatement)
